import { format } from 'date-fns';
import { useAtomValue, useSetAtom } from 'jotai';
import { useShowHints } from '@/api/mutations/useAuthMutations';
import { useUserQuery } from '@/api/queries/useAuth';
import { hintsAtom } from '@/atoms/hints';
import { tutorialActiveStepAtom } from '@/atoms/tutorial';
import { HINTS } from '@/constants';
import type { hintsBlock } from '@/enums';
import { useProjectId } from '@/hooks';

// Hints are switched off (the side-menu tour is the only one there is): it restarted on every hover, on every
// page, for every account (closing only cleared the in-memory atom). This is
// the only place a hint is ever started, so nothing below it — the popper, the
// atom, `HINTS`, the translations, the show-count request — runs while this is
// false. Kept rather than deleted in case the tour comes back.
const HINTS_ENABLED = false;

export const useHints = () => {
    const { data: user } = useUserQuery();
    const { mutate: showHints } = useShowHints();
    const setHint = useSetAtom(hintsAtom);
    const hintBlockState = useAtomValue(hintsAtom).block;
    const projectId = useProjectId();
    // A tutorial step owns the screen while it is watched (#1853).
    const tutorialStepActive = useAtomValue(tutorialActiveStepAtom) !== null;

    const displayHints = (type: hintsBlock) => {
        if (!HINTS_ENABLED || tutorialStepActive || hintBlockState || !projectId) return;

        if (user) {
            const { hint_show_count } = user;
            const hintBlock = hint_show_count.find(h => h.type === type);

            const displayHint = () => {
                showHints({ type });
                setHint({ block: type, item: Object.values(HINTS[type])[0]?.name ?? null });
            };

            if (!hintBlock) {
                displayHint();
                return;
            }

            if (hintBlock) {
                const time = new Date();
                const dayUPD = Number(format(new Date(hintBlock.updated_at), 'd'));
                if (time.getDate() !== dayUPD && hintBlock.count < 5) {
                    displayHint();
                }
            }
        }
    };

    return {
        displayHints,
    };
};
